import { useEffect, useRef } from "react";
import Placeholder from "@tiptap/extension-placeholder";
import { EditorContent, useEditor, type JSONContent } from "@tiptap/react";

import { tiptap } from "@/lib/tiptap";
import { cn } from "@/lib/utils";

// Plain-text input with @mention support — no bold, lists, or other formatting.
export const MentionTextInput = ({
  autoFocus,
  className,
  onChange,
  onSubmit,
  placeholder,
  value,
}: {
  autoFocus?: boolean;
  className?: string;
  onChange: (value: JSONContent) => void;
  // Called on Cmd/Ctrl+Enter; plain Enter inserts a newline.
  onSubmit?: () => void;
  placeholder?: string;
  value: JSONContent;
}) => {
  /* React Hooks */
  // The editor is created once, so it reads the latest callbacks through refs.
  const onChangeRef = useRef(onChange);
  const onSubmitRef = useRef(onSubmit);

  useEffect(() => {
    onChangeRef.current = onChange;
    onSubmitRef.current = onSubmit;
  }, [onChange, onSubmit]);

  const editor = useEditor({
    editorProps: {
      handleKeyDown: (_view, event) => {
        if (
          onSubmitRef.current &&
          event.key === "Enter" &&
          (event.metaKey || event.ctrlKey)
        ) {
          onSubmitRef.current();
          return true;
        }
        return false;
      },
    },
    extensions: [
      ...tiptap.generateMentionOnlyExtensions(),
      ...(placeholder ? [Placeholder.configure({ placeholder })] : []),
    ],
    onUpdate: ({ editor }) => {
      onChangeRef.current(editor.getJSON());
    },
  });

  // Sync external value changes (initial value, form reset) into the editor.
  // Self-typed updates round-trip to an identical JSON, so this no-ops for them.
  useEffect(() => {
    if (!editor) return;
    if (JSON.stringify(editor.getJSON()) !== JSON.stringify(value)) {
      editor.commands.setContent(value, false);
    }
  }, [editor, value]);

  useEffect(() => {
    if (autoFocus && editor) {
      editor.commands.focus("end");
    }
  }, [autoFocus, editor]);

  /* Render */
  const render = () => {
    return (
      <EditorContent
        className={cn(
          // Vertical padding comes from .ProseMirror's own inner padding
          // (index.css) — adding py here on top of it doubled the height.
          "max-h-32 min-h-10 w-full overflow-y-auto rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900",
          className,
        )}
        editor={editor}
        // Focus the editor when the user clicks anywhere in the input area.
        onClick={() => editor?.commands.focus()}
      />
    );
  };

  return render();
};
